/**
 * global.js — utilitaires partages, charges sur toutes les pages.
 * Pas de framework, pas de dependance. Chaque composant est un
 * custom element autonome enregistre dans son propre fichier.
 */

/** Annonce un message aux lecteurs d'ecran. */
export function announce(message) {
  const region = document.getElementById('rc-live-region');
  if (region) region.textContent = message;
}

/** Formate un montant en centimes avec le format monetaire de la boutique. */
export function money(cents) {
  return new Intl.NumberFormat(document.documentElement.lang, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR',
  }).format(cents / 100);
}

/**
 * Recupere une ou plusieurs sections rendues cote serveur.
 * @param {string[]} sections - identifiants de sections
 * @returns {Promise<Object>} map id -> HTML
 */
export async function fetchSections(sections) {
  const url = `${window.location.pathname}?sections=${sections.join(',')}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Section rendering failed: ${response.status}`);
  return response.json();
}

/** Remplace le contenu interne d'un element a partir d'un fragment HTML. */
export function replaceInner(target, html, selector) {
  if (!target) return;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const source = selector ? parsed.querySelector(selector) : parsed.body;
  if (source) target.innerHTML = source.innerHTML;
}

/** Piege le focus dans un conteneur, renvoie une fonction de liberation. */
export function trapFocus(container, initial) {
  const selector = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const previous = document.activeElement;

  function onKeydown(event) {
    if (event.key === 'Escape') {
      container.dispatchEvent(new CustomEvent('rc:dismiss'));
      return;
    }
    if (event.key !== 'Tab') return;

    const items = [...container.querySelectorAll(selector)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('keydown', onKeydown);
  (initial || container).focus({ preventScroll: true });

  return () => {
    document.removeEventListener('keydown', onKeydown);
    if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
  };
}

/**
 * Rend un conteneur a defilement horizontal glissable a la souris.
 *
 * Le tactile n'est jamais intercepte : le navigateur fait deja defiler le
 * conteneur avec l'inertie de la plateforme, et l'emuler donnerait un geste
 * plus raide sur telephone. On ne comble donc que le manque : la souris,
 * pour laquelle le navigateur n'offre rien.
 *
 * L'aimantation est suspendue pendant le geste, sinon elle se bat avec la
 * position tenue par le curseur et le rail tressaute sous la main.
 *
 * @param {HTMLElement} track - le conteneur qui defile
 * @returns {function} fonction de liberation
 */
export function dragScroll(track) {
  const THRESHOLD = 6;
  let active = false;
  let moved = 0;
  let startX = 0;
  let startLeft = 0;

  function onDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    active = true;
    moved = 0;
    startX = event.clientX;
    startLeft = track.scrollLeft;
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
  }

  function onMove(event) {
    if (!active) return;
    const dx = event.clientX - startX;
    if (Math.abs(dx) > THRESHOLD) {
      moved = Math.abs(dx);
      track.setPointerCapture(event.pointerId);
      track.dataset.dragging = 'true';
    }
    if (track.dataset.dragging) track.scrollLeft = startLeft - dx;
  }

  function onUp() {
    if (!active) return;
    active = false;
    delete track.dataset.dragging;
    track.style.scrollSnapType = '';
    track.style.scrollBehavior = '';
  }

  // Un glissement ne doit pas activer le lien sous le curseur au relachement.
  function onClick(event) {
    if (moved > THRESHOLD) {
      event.preventDefault();
      event.stopPropagation();
      moved = 0;
    }
  }

  track.addEventListener('pointerdown', onDown);
  track.addEventListener('pointermove', onMove);
  track.addEventListener('pointerup', onUp);
  track.addEventListener('pointercancel', onUp);
  track.addEventListener('pointerleave', onUp);
  track.addEventListener('click', onClick, true);

  return () => {
    track.removeEventListener('pointerdown', onDown);
    track.removeEventListener('pointermove', onMove);
    track.removeEventListener('pointerup', onUp);
    track.removeEventListener('pointercancel', onUp);
    track.removeEventListener('pointerleave', onUp);
    track.removeEventListener('click', onClick, true);
  };
}

/** Formulaires qui se soumettent au changement (tri de collection). */
document.querySelectorAll('[data-auto-submit]').forEach((form) => {
  form.addEventListener('change', () => form.submit());
});
